/**
 * SubscriptionsPanel · v2.6.7
 *
 * Rider-facing subscription store + active-plan card.
 *   - 4 plan tiers (Basic / Economy / Premier / Elite)
 *   - Card purchase routes through the same Square Web Payments SDK pathway
 *     used for ride payments. Cash purchase records the intent and waits for
 *     dispatch to mark it paid (Admin → Subscriptions tab).
 *   - All memberships are NON-REFUNDABLE — a tiny disclaimer is shown next to
 *     each Buy button.
 *
 * Outside-Weatherford discount % is per-plan (5/10/20/30 by default, configurable
 * server-side). Inside-Weatherford rides are free + consume 1 credit.
 */
import { useEffect, useState } from "react";
import customerApi from "@/lib/customerApi";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Check, Loader2, AlertTriangle, MapPin, Ticket, CalendarClock, DollarSign, History, Lock } from "lucide-react";
import { toast } from "sonner";
import { fmtUsd } from "@/lib/format";

const PLAN_ACCENT = {
  basic:   { ring: "border-zinc-600",     glow: "shadow-zinc-700/20",   chip: "text-zinc-300",     gradient: "from-zinc-900 to-zinc-950" },
  economy: { ring: "border-emerald-500",  glow: "shadow-emerald-500/30",chip: "text-emerald-300",  gradient: "from-emerald-950/40 to-zinc-950" },
  premier: { ring: "border-amber-400",    glow: "shadow-amber-400/30",  chip: "text-amber-300",    gradient: "from-amber-950/40 to-zinc-950" },
  elite:   { ring: "border-fuchsia-400",  glow: "shadow-fuchsia-400/40",chip: "text-fuchsia-300",  gradient: "from-fuchsia-950/40 to-zinc-950" },
};

export default function SubscriptionsPanel() {
  const [plans, setPlans] = useState([]);
  const [duration, setDuration] = useState(30);
  const [note, setNote] = useState("");
  const [mySub, setMySub] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openPlan, setOpenPlan] = useState(null); // plan object when buying
  const [payMethod, setPayMethod] = useState("card");
  const ccFee = 4.00;

  const reload = async () => {
    try { const r = await api.get("/subscriptions/plans"); setPlans(r.data.plans || []); setDuration(r.data.duration_days || 30); setNote(r.data.note || ""); } catch (e) { console.warn("plans load", e?.response?.status); }
    try { const r = await customerApi.get("/subscriptions/mine"); setMySub(r.data.subscription || null); } catch (e) { console.warn("mine load", e?.response?.status); }
    try { const r = await customerApi.get("/subscriptions/history"); setHistory(r.data.items || []); } catch (e) { console.warn("history load", e?.response?.status); }
  };
  useEffect(() => { reload(); }, []);

  const purchase = async () => {
    if (!openPlan) return;
    setBusy(true);
    try {
      const { data } = await customerApi.post("/subscriptions/purchase", {
        plan_id: openPlan.id,
        payment_method: payMethod,
        // Real Square tokenization would happen here when payMethod==='card'.
        // For v1 we record the intent; admin verifies the payment manually OR
        // the rider taps "Pay $X" inside the Square Web Payments SDK modal in
        // a follow-on phase (next iteration).
        square_payment_id: null,
      });
      if (payMethod === "cash") {
        toast.success("Subscription requested · dispatch will activate after cash payment");
      } else {
        toast.success(`Subscription purchased · ${data.subscription?.credits_remaining ?? openPlan.credits} credit(s) ready`);
      }
      setOpenPlan(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Purchase failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="subscriptions-panel">
      {/* Active subscription banner */}
      {mySub && mySub.status === "active" && (
        <Card className="bg-gradient-to-br from-amber-500/15 via-amber-400/5 to-zinc-950 border-amber-400/40 p-5" data-testid="active-sub-card">
          <div className="flex items-start gap-3">
            <div className="bg-amber-400/15 rounded-lg p-2.5"><Sparkles className="h-5 w-5 text-amber-400"/></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">Active Subscription</span>
                <Badge variant="outline" className="border-amber-400/60 text-amber-300 text-[10px] uppercase">{mySub.plan_name}</Badge>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3">
                <Pill k="Credits left" v={`${mySub.credits_remaining} / ${mySub.credits_total}`} bold/>
                <Pill k="Plan price" v={fmtUsd(mySub.price)}/>
                <Pill k="Outside disc." v={`${mySub.outside_discount_pct}%`}/>
                <Pill k="Expires" v={mySub.expires_at ? new Date(mySub.expires_at).toLocaleDateString() : "—"}/>
              </div>
              <p className="text-[11px] text-zinc-400 mt-3"><MapPin className="h-3 w-3 inline mr-1 text-amber-400"/>Each credit covers a single-passenger Weatherford ride at $0. Rides outside Weatherford get {mySub.outside_discount_pct}% off + use 1 credit.</p>
            </div>
          </div>
        </Card>
      )}
      {mySub && mySub.status === "pending" && (
        <Card className="bg-orange-500/10 border-orange-400/40 p-4" data-testid="pending-sub-card">
          <div className="flex items-start gap-2 text-orange-300">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0"/>
            <div className="text-xs">
              <div className="font-bold text-orange-200">{mySub.plan_name} · pending dispatch confirmation</div>
              <p className="mt-1 leading-snug">Drop by during dispatch hours to pay your <span className="font-bold">{fmtUsd(mySub.price)}</span> cash, or refresh once the dispatcher activates it. Your credits become available immediately on activation.</p>
            </div>
          </div>
        </Card>
      )}

      {/* Plan grid */}
      <div>
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2"><Ticket className="h-5 w-5 text-amber-400"/>Choose a Plan</h2>
            <p className="text-xs text-zinc-500 mt-0.5">{duration}-day membership · save long-term on regular Weatherford rides.</p>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-orange-300 flex items-center gap-1"><Lock className="h-3 w-3"/>Non-refundable</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {plans.map(p => {
            const a = PLAN_ACCENT[p.id] || PLAN_ACCENT.basic;
            const perRide = p.credits > 0 ? (p.price / p.credits) : p.price;
            const isCurrent = mySub?.plan_id === p.id && mySub?.status === "active";
            return (
              <Card key={p.id} className={`relative bg-gradient-to-br ${a.gradient} border-2 ${a.ring} shadow-lg ${a.glow} p-4 flex flex-col`} data-testid={`plan-card-${p.id}`}>
                {isCurrent && <Badge className="absolute -top-2 left-3 bg-amber-400 text-zinc-950 text-[9px] uppercase tracking-wider">Your Plan</Badge>}
                <div className={`text-[10px] uppercase tracking-[0.3em] font-bold ${a.chip}`}>{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="meter-display text-3xl font-extrabold text-zinc-100">{fmtUsd(p.price)}</span>
                  <span className="text-[10px] text-zinc-500">/ {duration}d</span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">≈ {fmtUsd(perRide)} per ride · {p.credits} credit{p.credits === 1 ? "" : "s"}</div>
                <ul className="text-xs text-zinc-300 space-y-1.5 mt-3 flex-1">
                  <li className="flex items-start gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0"/>{p.credits} ride{p.credits === 1 ? "" : "s"} in Weatherford ($0 each)</li>
                  <li className="flex items-start gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0"/>{p.outside_discount_pct}% off outside Weatherford</li>
                  <li className="flex items-start gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0"/>1 passenger · single rider</li>
                  <li className="flex items-start gap-1.5"><CalendarClock className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0"/>{duration}-day validity</li>
                </ul>
                <Button
                  data-testid={`plan-buy-${p.id}`}
                  disabled={isCurrent || busy}
                  onClick={() => { setOpenPlan(p); setPayMethod("card"); }}
                  className="mt-4 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold disabled:opacity-50"
                >
                  {isCurrent ? "Active" : <>Buy {p.name}</>}
                </Button>
                <div className="text-[9px] uppercase tracking-wider text-zinc-600 text-center mt-1">Non-refundable</div>
              </Card>
            );
          })}
        </div>
        <p className="text-[10px] text-zinc-500 mt-3 leading-relaxed">{note}</p>
      </div>

      {/* History */}
      {history.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="subs-history-card">
          <h3 className="font-bold flex items-center gap-2 mb-2"><History className="h-4 w-4 text-amber-400"/>Subscription history</h3>
          <div className="space-y-1.5 max-h-72 overflow-auto">
            {history.map(s => (
              <div key={s.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 text-xs flex items-center justify-between gap-3 flex-wrap" data-testid={`sub-row-${s.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold">{s.plan_name} · <span className="text-zinc-500">{new Date(s.created_at).toLocaleDateString()}</span></div>
                  <div className="text-[10px] text-zinc-500">{s.credits_used} / {s.credits_total} used · {s.payment_method} · {s.payment_status}</div>
                </div>
                <Badge variant="outline" className={
                  s.status === "active" ? "border-emerald-500/40 text-emerald-300" :
                  s.status === "pending" ? "border-orange-400/40 text-orange-300" :
                  s.status === "cancelled" ? "border-red-500/40 text-red-300" : "border-zinc-700 text-zinc-400"
                }>{s.status.toUpperCase()}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Purchase dialog */}
      <Dialog open={!!openPlan} onOpenChange={(o) => { if (!o) setOpenPlan(null); }}>
        <DialogContent className="bg-zinc-950 border-amber-400/40 text-zinc-100 max-w-md" data-testid="purchase-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2"><Sparkles className="h-5 w-5"/>Buy {openPlan?.name}</DialogTitle>
          </DialogHeader>
          {openPlan && (
            <div className="space-y-3 text-sm">
              <Card className="bg-zinc-900 border-zinc-800 p-3 space-y-1">
                <RowLine k="Plan" v={openPlan.name}/>
                <RowLine k="Credits" v={`${openPlan.credits} Weatherford ride${openPlan.credits === 1 ? "" : "s"}`}/>
                <RowLine k="Outside discount" v={`${openPlan.outside_discount_pct}% off`}/>
                <RowLine k="Validity" v={`${duration} days`}/>
                <div className="border-t border-zinc-800 my-1.5"/>
                <RowLine k="Plan price" v={fmtUsd(openPlan.price)}/>
                {payMethod === "card" && <RowLine k="Credit Card Processing Fee" v={fmtUsd(ccFee)}/>}
                <RowLine k="Total" v={fmtUsd(openPlan.price + (payMethod === "card" ? ccFee : 0))} bold/>
              </Card>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="pay-method-card"
                  onClick={() => setPayMethod("card")}
                  className={`rounded-md border p-3 text-left transition-all ${payMethod === "card" ? "border-amber-400 bg-amber-400/10" : "border-zinc-700 bg-zinc-900"}`}
                >
                  <div className="text-xs uppercase tracking-wider text-zinc-400">Pay now</div>
                  <div className="font-bold">Credit Card</div>
                  <div className="text-[10px] text-zinc-500">+{fmtUsd(ccFee)} processing fee</div>
                </button>
                <button
                  type="button"
                  data-testid="pay-method-cash"
                  onClick={() => setPayMethod("cash")}
                  className={`rounded-md border p-3 text-left transition-all ${payMethod === "cash" ? "border-amber-400 bg-amber-400/10" : "border-zinc-700 bg-zinc-900"}`}
                >
                  <div className="text-xs uppercase tracking-wider text-zinc-400">Cash at dispatch</div>
                  <div className="font-bold">Cash</div>
                  <div className="text-[10px] text-zinc-500">No processing fee · activates after dispatch confirms</div>
                </button>
              </div>
              <div className="bg-orange-500/10 border border-orange-400/30 rounded-md p-2 text-[11px] text-orange-300 leading-snug">
                <Lock className="h-3 w-3 inline mr-1"/>This subscription is <span className="font-bold">non-refundable</span>. Credits expire after {duration} days regardless of usage.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenPlan(null)} className="bg-zinc-950 border-zinc-700">Cancel</Button>
            <Button data-testid="purchase-confirm" onClick={purchase} disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
              {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Processing…</> : <><DollarSign className="h-4 w-4 mr-1"/>Confirm Purchase</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Pill({ k, v, bold }) {
  return (
    <div className="bg-zinc-950/60 border border-zinc-800/80 rounded p-2">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{k}</div>
      <div className={`mt-0.5 ${bold ? "font-bold text-amber-400 meter-display text-base" : "text-zinc-200 text-xs"}`}>{v}</div>
    </div>
  );
}
function RowLine({ k, v, bold }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-zinc-500">{k}</span>
      <span className={bold ? "font-bold text-amber-400 meter-display" : "text-zinc-200"}>{v}</span>
    </div>
  );
}
